"use client"

/**
 * Client-side auth guard — render-gating only.
 *
 * Responsibilities are split (design §1.1):
 *
 *   - **"Is there a session at all?"** — this guard. It calls
 *     ``useCurrentUser`` and decides whether to paint the protected
 *     subtree or a neutral loader. It does NOT redirect.
 *   - **"A 401 happened on a call"** — the global handler wired into the
 *     ``QueryCache``/``MutationCache`` (``lib/auth/session-expiry.ts``).
 *     That is the single place that reacts to a 401, so there is one rule
 *     in the codebase: *any 401 sends the user to ``/signin``.*
 *
 * Gate state machine over ``useCurrentUser()``:
 *
 *   | state                         | render                                    |
 *   |-------------------------------|-------------------------------------------|
 *   | success (has user)            | children                                  |
 *   | pending (probe in flight)     | <FullScreenLoader/>                        |
 *   | 401 error (handler redirects) | <FullScreenLoader/>                        |
 *   | non-401 error (500 / network) | children — let the page's error UI show   |
 *
 * Replacing the old optimistic render with a loader removes the
 * "empty dashboard flashes then redirects" symptom (§1.5): the dashboard
 * shell never mounts before auth resolves. The non-401-error row keeps
 * today's optimistic behaviour for the *backend-is-broken* case so an
 * ``/api/auth/me/`` 500 degrades to "dashboard with page-level errors"
 * rather than an infinite loader (``useCurrentUser`` already retries once
 * on non-401, so transient blips self-heal first).
 *
 * bfcache safety (§1.6): the old optimistic render existed to avoid a
 * captured mid-load skeleton stranding the user on back/forward restore.
 * The loader is safe here because ``useCurrentUser`` has a 5-minute
 * ``staleTime`` — once a session is confirmed, in-app navigations render
 * children immediately, so the loader is never captured into an
 * authenticated user's snapshots (it is only reachable on a cold load /
 * hard reload, which bypass bfcache). ``BfcacheRevalidator`` (mounted in
 * ``Providers``) additionally invalidates the ``me`` query on a
 * ``pageshow`` restore so the gate re-resolves to children or a redirect.
 *
 * Wired under ``Providers`` in ``app/layout.tsx``.
 */

import { useEffect, type ReactNode } from "react"
import { usePathname } from "next/navigation"

import { useCurrentUser } from "@/features/auth/hooks"
import { isPublicRoute } from "@/lib/auth/public-routes"
import { markSessionEstablished } from "@/lib/auth/session-expiry"
import { ApiError } from "@/lib/api/client"
import { setDatadogUser } from "@/lib/observability/datadog"
import { FullScreenLoader } from "@/components/full-screen-loader"

function AuthGate({ children }: { children: ReactNode }) {
  const { data: user, error, isPending, isSuccess } = useCurrentUser()

  useEffect(() => {
    if (isSuccess) {
      // Record that a real session existed so a later 401 is treated as a
      // mid-session expiry (gets the "signed out" notice) rather than a
      // first-time gate. Idempotent.
      markSessionEstablished()
    }
  }, [isSuccess])

  useEffect(() => {
    // Stamp the resolved user's id onto subsequent browser logs (usr.id).
    // Best-effort: a no-op until Datadog is initialized.
    if (user) {
      setDatadogUser(user.id)
    }
  }, [user])

  if (isSuccess) {
    return <>{children}</>
  }

  const unauthorized = error instanceof ApiError && error.status === 401
  if (isPending || unauthorized) {
    // Probe in flight, or a 401 the global handler is already navigating
    // away from — paint the loader, never the dashboard shell.
    return <FullScreenLoader />
  }

  // Non-401 error: let the protected subtree mount so its own error
  // states surface instead of trapping the user behind a loader.
  return <>{children}</>
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? "/"
  if (isPublicRoute(pathname)) {
    return <>{children}</>
  }
  return <AuthGate>{children}</AuthGate>
}
